import { FC, KeyboardEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { aiChatImages } from '../../Utils/images';
import { AiIcon } from '../AiIcon';
import { Specialist } from '../../Utils/specialists';
import { HistoryContextMenu } from './HistoryContextMenu';
import { HistoryItem } from './HistoryItem';
import {
  AiTitle,
  BottomGradient,
  Divider,
  EmptyHistory,
  GroupTitle,
  History,
  LoadingState,
  NewChatButton,
  NoResults,
  NoResultsIcon,
  NoResultsText,
  ScrollArea,
  SearchButton,
  SearchField,
  Section,
  SectionList,
  SidebarContainer,
  SidebarContent,
  SidebarHeader,
  SpecialistItem,
  SpecialistPersona,
  TopActions
} from './styles';

export interface HistoryThread {
  threadId: string;
  title: string;
}

export interface HistoryGroup {
  key: string;
  label: string;
  threads: HistoryThread[];
}

export interface SidebarProps {
  groups: HistoryGroup[];
  hasThreads: boolean;
  isLoading: boolean;
  search: string;
  isSearchOpen: boolean;
  selectedThreadId?: string;
  renamingThreadId?: string;
  specialists: Specialist[];
  isLibraryOpen: boolean;
  onSearchChange: (value: string) => void;
  onToggleSearch: () => void;
  onNewChat: () => void;
  onSelectThread: (threadId: string) => void;
  onOpenLibrary: () => void;
  onSelectSpecialist: (specialist: Specialist) => void;
  onStartRename: (threadId: string) => void;
  onSubmitRename: (threadId: string, name: string) => void;
  onCancelRename: () => void;
  onRequestDelete: (thread: HistoryThread) => void;
}

/** "Chat AI / Sidebar" do Figma. */
export const Sidebar: FC<SidebarProps> = ({
  groups,
  hasThreads,
  isLoading,
  search,
  isSearchOpen,
  selectedThreadId,
  renamingThreadId,
  specialists,
  isLibraryOpen,
  onSearchChange,
  onToggleSearch,
  onNewChat,
  onSelectThread,
  onOpenLibrary,
  onSelectSpecialist,
  onStartRename,
  onSubmitRename,
  onCancelRename,
  onRequestDelete
}) => {
  const { t } = useTranslation('activities');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [menu, setMenu] = useState<{ thread: HistoryThread; anchor: HTMLElement } | null>(
    null
  );

  const handleSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      onToggleSearch();
    }
  };

  const renderHistory = () => {
    if (isLoading) {
      return (
        <LoadingState>
          <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} />} />
        </LoadingState>
      );
    }

    if (!hasThreads) {
      return (
        <EmptyHistory>
          <AiIcon name="messages" />
          <span>{t('aiChat.noChats')}</span>
        </EmptyHistory>
      );
    }

    if (groups.length === 0) {
      return (
        <NoResults>
          <NoResultsIcon>
            <img src={aiChatImages.messageX} alt="" />
          </NoResultsIcon>
          <NoResultsText>
            <strong>{t('aiChat.noChatsFound')}</strong>
            <span>{t('aiChat.noChatsFoundHint')}</span>
          </NoResultsText>
        </NoResults>
      );
    }

    return groups.map((group) => (
      <Section key={group.key}>
        <GroupTitle>{group.label}</GroupTitle>
        <SectionList>
          {group.threads.map((thread) => (
            <HistoryItem
              key={thread.threadId}
              title={thread.title}
              selected={thread.threadId === selectedThreadId}
              menuOpen={menu?.thread.threadId === thread.threadId}
              renaming={thread.threadId === renamingThreadId}
              onSelect={() => onSelectThread(thread.threadId)}
              onOpenMenu={(anchor) =>
                setMenu((current) =>
                  current?.thread.threadId === thread.threadId ? null : { thread, anchor }
                )
              }
              onSubmitRename={(name) => onSubmitRename(thread.threadId, name)}
              onCancelRename={onCancelRename}
            />
          ))}
        </SectionList>
      </Section>
    ));
  };

  return (
    <SidebarContainer>
      <SidebarHeader>
        <AiTitle>
          <img src={aiChatImages.sparkles} alt="" />
          <span>{t('aiChat.title')}</span>
        </AiTitle>
      </SidebarHeader>

      <ScrollArea>
        <SidebarContent>
          {isSearchOpen ? (
            <SearchField $focused={isSearchFocused}>
              <AiIcon name={isSearchFocused ? 'searchFocused' : 'search'} />
              <input
                autoFocus
                value={search}
                placeholder={t('aiChat.searchChat')}
                aria-label={t('aiChat.searchChat')}
                onChange={(event) => onSearchChange(event.target.value)}
                onKeyDown={handleSearchKeyDown}
                onFocus={() => setIsSearchFocused(true)}
                onBlur={() => setIsSearchFocused(false)}
              />
              <button
                type="button"
                aria-label={t('aiChat.closeSearch')}
                onClick={onToggleSearch}
              >
                <AiIcon name="clear" />
              </button>
            </SearchField>
          ) : (
            <TopActions>
              <NewChatButton type="button" onClick={onNewChat}>
                <AiIcon name="newChat" />
                {t('aiChat.newChatButton')}
              </NewChatButton>
              <SearchButton
                type="button"
                aria-label={t('aiChat.searchChat')}
                onClick={onToggleSearch}
              >
                <AiIcon name="search" />
              </SearchButton>
            </TopActions>
          )}

          <Divider />

          {!isSearchOpen && (
            <>
              <Section>
                <GroupTitle>{t('aiChat.specialists')}</GroupTitle>
                <SectionList>
                  <SpecialistItem
                    type="button"
                    $active={isLibraryOpen}
                    onClick={onOpenLibrary}
                  >
                    <AiIcon name="specialists" />
                    <span>{t('aiChat.library.title')}</span>
                  </SpecialistItem>
                  {specialists.map((specialist) => (
                    <SpecialistItem
                      key={specialist.id}
                      type="button"
                      title={t(specialist.labelKey)}
                      onClick={() => onSelectSpecialist(specialist)}
                    >
                      <SpecialistPersona src={specialist.image} alt="" />
                      <span>{t(specialist.labelKey)}</span>
                    </SpecialistItem>
                  ))}
                </SectionList>
              </Section>
              <Divider />
            </>
          )}

          <History>{renderHistory()}</History>
        </SidebarContent>
        <BottomGradient />
      </ScrollArea>

      {menu && (
        <HistoryContextMenu
          anchor={menu.anchor}
          onClose={() => setMenu(null)}
          onRename={() => {
            onStartRename(menu.thread.threadId);
            setMenu(null);
          }}
          onDelete={() => {
            onRequestDelete(menu.thread);
            setMenu(null);
          }}
        />
      )}
    </SidebarContainer>
  );
};
